import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { type ReactNode, Suspense } from "react";
import { useTranslation } from "@/services/i18n/client";
import { route, RouteKeys } from "@/services/routing/routes";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import RootLayout from "./layouts/RootLayout";
import PublicLayout from "./layouts/PublicLayout";
import AppLayout from "./layouts/AppLayout";
import AdminLayout from "./layouts/AdminLayout";
import { protectedLoader } from "./lib/auth/protected-loader";
import { dashboardLoader } from "./lib/auth/dashboard-loader";
import { guestLoader } from "./lib/auth/guest-loader";
import { homeLoader } from "./lib/auth/home-loader";
import { ROLES } from "./lib/auth/roles";

// Lazy-loaded pages (code splitting with retry for stale chunk recovery)
const HomePage = lazyWithRetry(() => import("./pages/HomePage"));
const PricingPage = lazyWithRetry(() => import("./pages/PricingPage"));
const ShopifyConnectPage = lazyWithRetry(() => import("./pages/ShopifyConnectPage"));
const ShopifyIntegrationPage = lazyWithRetry(
  () => import("./features/scanner/pages/shopify-integration-page")
);
const DashboardPage = lazyWithRetry(() => import("./features/dashboard/pages/dashboard-page"));
const SignInPage = lazyWithRetry(() => import("./pages/SignInPage"));
const SignUpPage = lazyWithRetry(() => import("./pages/SignUpPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const PasswordChangePage = lazyWithRetry(() => import("./pages/PasswordChangePage"));
const ConfirmEmailPage = lazyWithRetry(() => import("./pages/ConfirmEmailPage"));
const ConfirmNewEmailPage = lazyWithRetry(() => import("./pages/ConfirmNewEmailPage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./pages/PrivacyPolicyPage"));
const DataPrivacyPage = lazyWithRetry(() => import("./pages/DataPrivacyPage"));
const TermsOfServicePage = lazyWithRetry(() => import("./pages/TermsOfServicePage"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));
const ProfileEditPage = lazyWithRetry(() => import("./pages/ProfileEditPage"));
const AdminPanelPage = lazyWithRetry(() => import("./pages/AdminPanelPage"));
const AdminUsersPage = lazyWithRetry(() => import("./pages/AdminUsersPage"));
const AdminUsersCreatePage = lazyWithRetry(() => import("./pages/AdminUsersCreatePage"));
const AdminUsersEditPage = lazyWithRetry(() => import("./pages/AdminUsersEditPage"));
const ScannerPage = lazyWithRetry(() => import("./features/scanner/pages/scanner-page"));
const SendingPage = lazyWithRetry(() => import("./features/scanner/pages/sending-page"));
const PackageDetailPage = lazyWithRetry(
  () => import("./features/scanner/pages/package-detail-page")
);
const PackagesOverviewPage = lazyWithRetry(
  () => import("./features/scanner/pages/packages-overview-page")
);
const LocationsPage = lazyWithRetry(() => import("./features/locations/pages/locations-page"));
const LocationDetailPage = lazyWithRetry(
  () => import("./features/locations/pages/location-detail-page")
);
const EditLocationPage = lazyWithRetry(
  () => import("./features/locations/pages/edit-location-page")
);
const ContractsPage = lazyWithRetry(() => import("./features/contracts/pages/contracts-page"));
const ContractDetailPage = lazyWithRetry(
  () => import("./features/contracts/pages/contract-detail-page")
);
const CompaniesAdminPage = lazyWithRetry(
  () => import("./features/admin/pages/companies-admin-page")
);
const PackagesAdminPage = lazyWithRetry(() => import("./features/admin/pages/packages-admin-page"));
const ContractsAdminPage = lazyWithRetry(
  () => import("./features/admin/pages/contracts-admin-page")
);
const PackagingTypesAdminPage = lazyWithRetry(
  () => import("./features/admin/pages/packaging-types-admin-page")
);
const BillingPage = lazyWithRetry(() => import("./features/billing/pages/billing-page"));
const BillingSuccessPage = lazyWithRetry(
  () => import("./features/billing/pages/billing-success-page")
);
const AnalyticsPage = lazyWithRetry(() => import("./features/analytics/pages/analytics-page"));
const ExternalDestinationsPage = lazyWithRetry(
  () => import("./features/external-destinations/pages/external-destinations-page")
);
const ExternalDestinationCreatePage = lazyWithRetry(
  () => import("./features/external-destinations/pages/external-destination-create-page")
);
const ExternalDestinationDetailPage = lazyWithRetry(
  () => import("./features/external-destinations/pages/external-destination-detail-page")
);
const ExternalDestinationEditPage = lazyWithRetry(
  () => import("./features/external-destinations/pages/external-destination-edit-page")
);
const DestinationsPage = lazyWithRetry(
  () => import("./features/destinations/pages/destinations-page")
);
const DefectReportingPage = lazyWithRetry(
  () => import("./features/support/pages/defect-reporting-page")
);
const UsersPage = lazyWithRetry(() => import("./features/users/pages/users-page"));
const RedistributionPlanPage = lazyWithRetry(
  () => import("./features/inventory/pages/redistribution-plan-page")
);
const ReorderPage = lazyWithRetry(() => import("./features/inventory/pages/reorder-page"));
const TrackingPage = lazyWithRetry(() => import("./features/tracking/pages/tracking-page"));
const MonitoringPage = lazyWithRetry(() => import("./features/monitoring/pages/monitoring-page"));
const HelpCenterPage = lazyWithRetry(() => import("./features/help/pages/help-center-page"));

function SuspenseWrapper({ children }: { children: ReactNode }) {
  const { t } = useTranslation("common");
  const fallback = <div>{t("loading")}</div>;
  return <Suspense fallback={fallback}>{children}</Suspense>;
}

// Parameterized backward-compat redirect components
function LocationDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/destinations/locations/${id}`} replace />;
}
function LocationEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/destinations/locations/${id}/edit`} replace />;
}
function ExternalDestinationEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/destinations/external/${id}/edit`} replace />;
}

function ExternalDestinationDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/destinations/external/${id}`} replace />;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      // Public routes with PublicLayout (navigation with Sign In/Sign Up)
      {
        element: <PublicLayout />,
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <HomePage />
              </SuspenseWrapper>
            ),
            loader: homeLoader(),
          },
          {
            path: "pricing",
            element: (
              <SuspenseWrapper>
                <PricingPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "privacy-policy",
            element: (
              <SuspenseWrapper>
                <PrivacyPolicyPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "data-privacy",
            element: (
              <SuspenseWrapper>
                <DataPrivacyPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "terms-of-service",
            element: (
              <SuspenseWrapper>
                <TermsOfServicePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "sign-in",
            element: (
              <SuspenseWrapper>
                <SignInPage />
              </SuspenseWrapper>
            ),
            loader: guestLoader(),
          },
          {
            path: "sign-up",
            element: (
              <SuspenseWrapper>
                <SignUpPage />
              </SuspenseWrapper>
            ),
            loader: guestLoader(),
          },
          {
            path: "forgot-password",
            element: (
              <SuspenseWrapper>
                <ForgotPasswordPage />
              </SuspenseWrapper>
            ),
            loader: guestLoader(),
          },
        ],
      },

      // Auth flow pages (no navigation needed)
      {
        path: "confirm-email",
        element: (
          <SuspenseWrapper>
            <ConfirmEmailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "confirm-new-email",
        element: (
          <SuspenseWrapper>
            <ConfirmNewEmailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "password-change",
        element: (
          <SuspenseWrapper>
            <PasswordChangePage />
          </SuspenseWrapper>
        ),
      },

      // Customer domain (/app)
      {
        path: "app",
        element: <AppLayout />,
        loader: protectedLoader({
          requiredRoles: [
            ROLES.USER,
            ROLES.COMPANY_ADMIN,
            ROLES.COMPANY_MANAGER,
            ROLES.COMPANY_SCANNER,
          ],
        }),
        children: [
          {
            index: true,
            element: <Navigate to={route(RouteKeys.DASHBOARD)} replace />,
          },
          {
            path: "dashboard",
            element: (
              <SuspenseWrapper>
                <DashboardPage />
              </SuspenseWrapper>
            ),
            // Per ADR 0002 / D-02, dashboardLoader does NOT redirect for
            // COMPANY_SCANNER — they see role-trimmed widgets via <RoleGate>.
            loader: dashboardLoader(),
          },
          {
            path: "scanner",
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <ScannerPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "send",
                element: (
                  <SuspenseWrapper>
                    <SendingPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "packages",
                element: (
                  <SuspenseWrapper>
                    <PackagesOverviewPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "packages/:packageId",
                element: (
                  <SuspenseWrapper>
                    <PackageDetailPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },
          {
            path: "contracts",
            element: (
              <SuspenseWrapper>
                <ContractsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "contracts/:id",
            element: (
              <SuspenseWrapper>
                <ContractDetailPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "destinations",
            children: [
              {
                index: true,
                element: (
                  <SuspenseWrapper>
                    <DestinationsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "locations",
                element: (
                  <SuspenseWrapper>
                    <LocationsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "locations/:id",
                element: (
                  <SuspenseWrapper>
                    <LocationDetailPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "locations/:id/edit",
                element: (
                  <SuspenseWrapper>
                    <EditLocationPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "external",
                element: (
                  <SuspenseWrapper>
                    <ExternalDestinationsPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "external/new",
                element: (
                  <SuspenseWrapper>
                    <ExternalDestinationCreatePage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "external/:id",
                element: (
                  <SuspenseWrapper>
                    <ExternalDestinationDetailPage />
                  </SuspenseWrapper>
                ),
              },
              {
                path: "external/:id/edit",
                element: (
                  <SuspenseWrapper>
                    <ExternalDestinationEditPage />
                  </SuspenseWrapper>
                ),
              },
            ],
          },
          // Backward compat redirects for old /app/locations/* and /app/external-destinations/* paths
          {
            path: "locations",
            element: <Navigate to="/app/destinations/locations" replace />,
          },
          {
            path: "locations/:id",
            element: <LocationDetailRedirect />,
          },
          {
            path: "locations/:id/edit",
            element: <LocationEditRedirect />,
          },
          {
            path: "external-destinations",
            element: <Navigate to="/app/destinations/external" replace />,
          },
          {
            path: "external-destinations/new",
            element: <Navigate to="/app/destinations/external/new" replace />,
          },
          {
            path: "external-destinations/:id",
            element: <ExternalDestinationDetailRedirect />,
          },
          {
            path: "external-destinations/:id/edit",
            element: <ExternalDestinationEditRedirect />,
          },
          {
            // Platform Shopify Integration page: observe/manage the connection
            // and review orders completed with reusable packaging. Merchant
            // storefront config lives in the embedded app, not here.
            path: "shopify",
            element: (
              <SuspenseWrapper>
                <ShopifyIntegrationPage />
              </SuspenseWrapper>
            ),
            loader: protectedLoader({ requiredRoles: [ROLES.COMPANY_ADMIN] }),
          },
          {
            // Headless one-time OAuth handshake initiated from the embedded
            // Shopify app (?shop=<domain>). Renders no config UI — that lives
            // entirely in the embedded app. Protected so the merchant signs in
            // first; returnTo preserves ?shop=.
            path: "shopify/connect",
            element: (
              <SuspenseWrapper>
                <ShopifyConnectPage />
              </SuspenseWrapper>
            ),
            loader: protectedLoader({ requiredRoles: [ROLES.COMPANY_ADMIN] }),
          },
          {
            path: "profile",
            element: (
              <SuspenseWrapper>
                <ProfilePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "profile/edit",
            element: (
              <SuspenseWrapper>
                <ProfileEditPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "billing",
            element: (
              <SuspenseWrapper>
                <BillingPage />
              </SuspenseWrapper>
            ),
            loader: protectedLoader({ requiredRoles: [ROLES.COMPANY_ADMIN] }),
          },
          {
            path: "billing/success",
            element: (
              <SuspenseWrapper>
                <BillingSuccessPage />
              </SuspenseWrapper>
            ),
            loader: protectedLoader({ requiredRoles: [ROLES.COMPANY_ADMIN] }),
          },
          {
            path: "analytics",
            element: (
              <SuspenseWrapper>
                <AnalyticsPage />
              </SuspenseWrapper>
            ),
            loader: protectedLoader({
              requiredRoles: [ROLES.ADMIN, ROLES.COMPANY_ADMIN, ROLES.COMPANY_MANAGER],
            }),
          },
          {
            path: "support/defect-reporting",
            element: (
              <SuspenseWrapper>
                <DefectReportingPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "users",
            element: (
              <SuspenseWrapper>
                <UsersPage />
              </SuspenseWrapper>
            ),
            loader: protectedLoader({ requiredRoles: [ROLES.COMPANY_ADMIN] }),
          },
          {
            path: "inventory/redistribute",
            element: (
              <SuspenseWrapper>
                <RedistributionPlanPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "inventory/reorder",
            element: (
              <SuspenseWrapper>
                <ReorderPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "help",
            element: (
              <SuspenseWrapper>
                <HelpCenterPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },

      // Admin domain (/admin)
      {
        path: "admin",
        element: <AdminLayout />,
        loader: protectedLoader({ requiredRoles: [ROLES.ADMIN] }),
        children: [
          {
            index: true,
            element: <Navigate to={route(RouteKeys.ADMIN_DASHBOARD)} replace />,
          },
          {
            path: "dashboard",
            element: (
              <SuspenseWrapper>
                <AdminPanelPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "users",
            element: (
              <SuspenseWrapper>
                <AdminUsersPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "users/create",
            element: (
              <SuspenseWrapper>
                <AdminUsersCreatePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "users/edit/:id",
            element: (
              <SuspenseWrapper>
                <AdminUsersEditPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "companies",
            element: (
              <SuspenseWrapper>
                <CompaniesAdminPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "packages",
            element: (
              <SuspenseWrapper>
                <PackagesAdminPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "contracts",
            element: (
              <SuspenseWrapper>
                <ContractsAdminPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "users-management",
            element: <Navigate to={route(RouteKeys.ADMIN_USERS)} replace />,
          },
          {
            path: "packaging-types",
            element: (
              <SuspenseWrapper>
                <PackagingTypesAdminPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "monitoring",
            element: (
              <SuspenseWrapper>
                <MonitoringPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },

      // Tracking page — public, no layout, no auth (Phase 15)
      {
        path: "track/:token",
        element: (
          <SuspenseWrapper>
            <TrackingPage />
          </SuspenseWrapper>
        ),
      },

      // Backward compatibility redirects
      {
        path: "scanning",
        element: <Navigate to={route(RouteKeys.SCANNER)} replace />,
      },
      {
        path: "scanning/packages/:packageId",
        element: <Navigate to="/app/scanner/packages/:packageId" replace />,
      },
      {
        path: "contracts",
        element: <Navigate to={route(RouteKeys.CONTRACTS)} replace />,
      },
      {
        path: "contracts/:id",
        element: <Navigate to="/app/contracts/:id" replace />,
      },
      {
        path: "locations",
        element: <Navigate to={route(RouteKeys.LOCATIONS)} replace />,
      },
      {
        path: "locations/:id",
        element: <LocationDetailRedirect />,
      },
      {
        path: "locations/:id/edit",
        element: <LocationEditRedirect />,
      },
      {
        path: "profile",
        element: <Navigate to={route(RouteKeys.PROFILE)} replace />,
      },
      {
        path: "profile/edit",
        element: <Navigate to={route(RouteKeys.PROFILE_EDIT)} replace />,
      },
      {
        path: "admin-panel",
        element: <Navigate to={route(RouteKeys.ADMIN_DASHBOARD)} replace />,
      },
      {
        path: "admin-panel/users",
        element: <Navigate to={route(RouteKeys.ADMIN_USERS)} replace />,
      },
      {
        path: "admin-panel/users/create",
        element: <Navigate to={route(RouteKeys.ADMIN_USERS_CREATE)} replace />,
      },
      {
        path: "admin-panel/users/edit/:id",
        element: <Navigate to="/admin/users/edit/:id" replace />,
      },
    ],
  },
]);
